import { Link } from "react-router-dom";
import { BadgeCheck, CalendarDays, Clock, Eye, Gavel, ShieldAlert, Timer, TrendingUp, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { money } from "@/lib/format";
import { compact, longDate, statsBadge, statusMeta, timeLeft, watchTime } from "@/lib/market";

/** Claimed-vs-verified pill, shared by every market surface. */
export const StatsStatusBadge = ({ status }) =>
    status === "verified" ? (
        <Badge className="bg-brand/15 text-brand border-brand/30 hover:bg-brand/15">
            <BadgeCheck className="h-3 w-3 mr-1" /> Verified
        </Badge>
    ) : (
        <Badge variant="secondary" className="text-muted-foreground">
            <ShieldAlert className="h-3 w-3 mr-1" /> Claimed
        </Badge>
    );


const Metric = ({ icon: Icon, label, value }) => (
    <div className="rounded-lg border border-border bg-surface-2/60 px-2.5 py-2">
        <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
            <Icon className="h-3 w-3" /> {label}
        </div>
        <div className="font-heading text-sm font-semibold tabular mt-0.5">{value}</div>
    </div>
);

/**
 * Auction card. Always shows name, niche, average views, slot, current bid and time
 * left, because a sponsor should never have to open a listing to compare inventory.
 */
export const MarketCard = ({ listing }) => {
    const status = statusMeta(listing.status);
    const live = listing.status === "live";
    const fits = listing.sponsor_fits || [];

    return (
        <article
            data-testid={`market-card-${listing.id}`}
            className="rounded-2xl border border-border bg-card p-4 flex flex-col gap-3 hover:border-white/15 transition-colors"
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <Link
                        to={`/c/${listing.creator_handle}`}
                        className="font-heading font-semibold truncate hover:text-brand transition-colors"
                    >
                        {listing.creator_name}
                    </Link>
                    <div className="text-xs text-muted-foreground truncate">
                        @{listing.creator_handle}
                        {listing.city ? ` · ${listing.city}` : ""}
                    </div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                    <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">
                        {statsBadge(listing.stats_status)}
                    </Badge>
                    {!live ? (
                        <Badge
                            variant="outline"
                            className={`text-[10px] ${status.tone === "heat" ? "text-heat border-heat/40" : "text-muted-foreground"}`}
                        >
                            {status.label}
                        </Badge>
                    ) : null}
                </div>
            </div>

            <div>
                <Link
                    to={`/a/${listing.id}`}
                    className="font-medium leading-snug hover:text-brand transition-colors line-clamp-2"
                >
                    {listing.title}
                </Link>
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    <Badge variant="secondary" className="text-[10px]">{listing.niche || "Uncategorised"}</Badge>
                    <Badge className="text-[10px] bg-brand/15 text-brand border-brand/30 hover:bg-brand/15">
                        {listing.slot_label}
                    </Badge>
                    {listing.event_name ? (
                        <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                            <CalendarDays className="h-3 w-3" /> {listing.event_name}
                            {listing.event_date ? ` · ${longDate(listing.event_date)}` : ""}
                        </span>
                    ) : null}
                </div>
            </div>

            <div className="grid grid-cols-3 gap-1.5">
                <Metric icon={Eye} label="Avg views" value={compact(listing.avg_views_30d)} />
                <Metric icon={Users} label="Followers" value={compact(listing.followers)} />
                <Metric icon={Clock} label="Watch" value={watchTime(listing.avg_watch_time_sec)} />
            </div>

            <div className="flex items-end justify-between gap-3 mt-auto pt-3 border-t border-border">
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1">
                        <Gavel className="h-3 w-3" />
                        {listing.has_bids ? "Current bid" : "Opening bid"}
                    </div>
                    <div className="font-heading text-xl font-bold tabular">
                        {money(listing.current_bid_usd)}
                    </div>
                    {listing.has_bids && listing.top_sponsor ? (
                        <div className="text-[11px] text-muted-foreground truncate">
                            {listing.top_sponsor} leads · next {money(listing.next_bid_usd)}
                        </div>
                    ) : null}
                </div>
                <div className="text-right shrink-0">
                    <div className="inline-flex items-center gap-1 text-xs font-semibold tabular text-heat">
                        <Timer className="h-3.5 w-3.5" />
                        {timeLeft(listing.seconds_left)}
                    </div>
                    <Link
                        to={`/a/${listing.id}`}
                        data-testid={`market-bid-${listing.id}`}
                        className="block mt-1.5 text-center text-xs font-semibold rounded-md px-3 py-2 bg-brand text-primary-foreground hover:bg-brand-dim transition-colors"
                    >
                        {live ? "Bid" : "View auction"} <TrendingUp className="h-3.5 w-3.5 inline ml-0.5" />
                    </Link>
                </div>
            </div>
        </article>
    );
};

export default MarketCard;
